import React from "react";
import {
  Phone,
  Mail,
  Shield,
  FileText,
  Globe2,
  UserCheck,
  AlertTriangle,
  Lock,
} from "lucide-react";

const PrivacyPage = () => {
  return (
    <div className="min-h-screen bg-slate-50 text-slate-800 flex flex-col">
      <main className="flex-1 max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white shadow-sm border border-slate-200 rounded-2xl p-6 md:p-8">
          {/* Título principal */}
          <header className="mb-6 border-b border-slate-200 pb-4">
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2">
              Aviso de Privacidad Integral – SolCont MX
            </h1>
            <p className="text-xs text-slate-500">
              Última actualización: 01 de enero de 2025
            </p>
            <p className="mt-3 text-sm text-slate-700">
              Este Aviso de Privacidad se emite en cumplimiento de la{" "}
              <strong>
                Ley Federal de Protección de Datos Personales en Posesión de los
                Particulares (LFPDPPP)
              </strong>
              , su Reglamento, los Lineamientos del INAI y demás disposiciones
              aplicables vigentes a la fecha de última actualización. Su
              finalidad es informar, de forma clara y detallada, cómo SolCont MX
              recopila, utiliza, protege y, en su caso, comparte sus datos
              personales.
            </p>
          </header>

          {/* 1. Responsable */}
          <section className="mb-6" id="responsable">
            <h2 className="text-lg font-semibold text-slate-900 mb-2">
              1. Responsable del tratamiento de datos personales
            </h2>
            <p className="text-sm text-slate-700 mb-3">
              El responsable del tratamiento de sus datos personales es{" "}
              <strong>SolCont MX</strong>, despacho de servicios contables,
              fiscales y administrativos con operaciones en los Estados Unidos
              Mexicanos.
            </p>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 flex gap-3">
                <div className="mt-1">
                  <Shield className="w-6 h-6 text-sky-600" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-slate-900">
                    Responsable de Datos Personales
                  </p>
                  <p className="text-xs text-slate-600 mt-1">
                    SolCont MX es el responsable directo del uso, protección y
                    tratamiento de sus datos personales para las finalidades
                    descritas en este Aviso de Privacidad.
                  </p>
                </div>
              </div>

              <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 flex flex-col gap-2">
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-sky-600" />
                  <span className="text-xs text-slate-700">
                    <strong>Teléfono:</strong> 238 101 3593
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-sky-600" />
                  <span className="text-xs text-slate-700">
                    <strong>Correo electrónico:</strong>{" "}
                    <a
                      href="mailto:isaias.lezama@solcont.com.mx"
                      className="text-sky-600 hover:underline"
                    >
                      isaias.lezama@solcont.com.mx
                    </a>
                  </span>
                </div>

              </div>
            </div>
          </section>

          {/* 2. Alcance del Aviso */}
          <section className="mb-6" id="alcance">
            <h2 className="text-lg font-semibold text-slate-900 mb-2">
              2. Alcance y aceptación del Aviso de Privacidad
            </h2>
            <p className="text-sm text-slate-700 mb-2">
              Este Aviso de Privacidad aplica a todas las personas físicas que,
              directa o indirectamente, proporcionen datos personales a SolCont
              MX, ya sea en calidad de clientes, prospectos, representantes
              legales, obligados solidarios, contactos administrativos o
              cualquier otra figura relacionada con la prestación de nuestros
              servicios.
            </p>
            <p className="text-sm text-slate-700 mb-2">
              Al utilizar nuestros servicios, compartir documentación fiscal,
              firmar contratos, intercambiar información por medios
              electrónicos, acudir a nuestras instalaciones o comunicarse por
              cualquier canal (teléfono, correo electrónico, mensajería
              instantánea, etc.), usted declara que:
            </p>
            <ul className="list-disc list-inside text-sm text-slate-700 space-y-1">
              <li>Ha leído este Aviso de Privacidad.</li>
              <li>Comprende el alcance del tratamiento de sus datos.</li>
              <li>
                Otorga su consentimiento para el uso de sus datos conforme a las
                finalidades aquí descritas, salvo que manifieste lo contrario
                mediante el ejercicio de sus derechos ARCO u otros mecanismos
                previstos por la ley.
              </li>
            </ul>
          </section>

          {/* 3. Datos personales */}
          <section className="mb-6" id="datos">
            <h2 className="text-lg font-semibold text-slate-900 mb-2">
              3. Datos personales que recabamos
            </h2>
            <p className="text-sm text-slate-700 mb-3">
              SolCont MX únicamente recaba los datos necesarios para prestar sus
              servicios profesionales y cumplir con obligaciones legales y
              contractuales. Entre ellos se encuentran, de forma enunciativa mas
              no limitativa:
            </p>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                <p className="text-sm font-semibold text-slate-900 mb-1">
                  Datos de identificación
                </p>
                <ul className="list-disc list-inside text-xs text-slate-700 space-y-1">
                  <li>Nombre completo</li>
                  <li>RFC y, en su caso, CURP</li>
                  <li>Firma (autógrafa o electrónica)</li>
                  <li>Fecha de nacimiento (cuando sea necesaria)</li>
                </ul>
              </div>

              <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                <p className="text-sm font-semibold text-slate-900 mb-1">
                  Datos de contacto y ubicación
                </p>
                <ul className="list-disc list-inside text-xs text-slate-700 space-y-1">
                  <li>Domicilio fiscal</li>
                  <li>Teléfono de contacto</li>
                  <li>Correo electrónico</li>
                  <li>Datos de contacto administrativo</li>
                </ul>
              </div>

              <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                <p className="text-sm font-semibold text-slate-900 mb-1">
                  Datos fiscales y financieros
                </p>
                <ul className="list-disc list-inside text-xs text-slate-700 space-y-1">
                  <li>CFDI emitidos y recibidos</li>
                  <li>Estados de cuenta bancarios</li>
                  <li>Información de ingresos y deducciones</li>
                  <li>
                    Información requerida por el SAT u otras autoridades para
                    fines de cumplimiento.
                  </li>
                </ul>
              </div>

              <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                <p className="text-sm font-semibold text-slate-900 mb-1">
                  Datos de terceros relacionados
                </p>
                <ul className="list-disc list-inside text-xs text-slate-700 space-y-1">
                  <li>
                    Nombre y datos de representantes legales u obligados
                    solidarios
                  </li>
                  <li>
                    Datos de contactos de facturación o administración
                  </li>
                  <li>
                    Información de proveedores o clientes relacionada con la
                    contabilidad del contribuyente
                  </li>
                </ul>
              </div>
            </div>

            <p className="text-xs text-slate-500 mt-3 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-500" />
              En caso de requerirse datos considerados como sensibles por la
              LFPDPPP, estos serán recabados y tratados bajo medidas reforzadas
              de seguridad y confidencialidad, y solo cuando resulten
              estrictamente indispensables.
            </p>
          </section>

          {/* 4. Finalidades */}
          <section className="mb-6" id="finalidades">
            <h2 className="text-lg font-semibold text-slate-900 mb-2">
              4. Finalidades del tratamiento de datos
            </h2>

            <p className="text-sm text-slate-700 mb-2">
              Los datos personales que usted proporciona a SolCont MX serán
              utilizados para las siguientes <strong>finalidades primarias</strong>,
              necesarias para la existencia y cumplimiento de la relación
              contractual:
            </p>

            <ul className="list-disc list-inside text-sm text-slate-700 space-y-1 mb-3">
              <li>Prestación de servicios contables y fiscales.</li>
              <li>
                Elaboración y presentación de declaraciones ante el SAT y otras
                autoridades.
              </li>
              <li>
                Cumplimiento de obligaciones legales, fiscales y administrativas
                aplicables.
              </li>
              <li>
                Emisión de CFDI por servicios de honorarios y administración de
                facturación.
              </li>
              <li>
                Preparación de papeles de trabajo, reportes internos y soportes
                documentales.
              </li>
              <li>Atención y seguimiento a consultas relacionadas con el servicio.</li>
            </ul>

            <p className="text-sm text-slate-700 mb-2">
              Adicionalmente, podremos utilizar su información para{" "}
              <strong>finalidades secundarias</strong> que no son necesarias
              para el servicio principal, pero que nos ayudan a brindar una
              mejor experiencia:
            </p>
            <ul className="list-disc list-inside text-sm text-slate-700 space-y-1">
              <li>Envío de recordatorios de vencimientos fiscales.</li>
              <li>
                Envío de avisos administrativos o información relevante sobre
                cambios legales fiscales.
              </li>
              <li>
                Encuestas de calidad del servicio o mejoras en procesos
                internos.
              </li>
            </ul>

            <p className="text-xs text-slate-500 mt-3">
              Usted puede oponerse a las finalidades secundarias en cualquier
              momento enviando un correo a{" "}
              <span className="font-semibold">
                isaias.lezama@solcont.com.mx
              </span>
              , sin que esto afecte la prestación de los servicios principales.
            </p>
          </section>

          {/* 5. Fundamento legal */}
          <section className="mb-6" id="fundamento-legal">
            <h2 className="text-lg font-semibold text-slate-900 mb-2">
              5. Fundamento legal y ley aplicable a la fecha
            </h2>
            <p className="text-sm text-slate-700 mb-2">
              El tratamiento de sus datos personales se realiza con base en lo
              dispuesto por:
            </p>
            <ul className="list-disc list-inside text-sm text-slate-700 space-y-1">
              <li>
                La{" "}
                <strong>
                  Ley Federal de Protección de Datos Personales en Posesión de
                  los Particulares (LFPDPPP)
                </strong>
                .
              </li>
              <li>Su Reglamento.</li>
              <li>
                Los Lineamientos del Aviso de Privacidad emitidos por el INAI.
              </li>
              <li>
                Legislación fiscal vigente aplicable a la fecha (incluyendo
                disposiciones del Código Fiscal de la Federación y leyes
                complementarias).
              </li>
            </ul>
            <p className="text-sm text-slate-700 mt-2">
              A la fecha de última actualización de este Aviso de Privacidad (
              <strong>01 de enero de 2025</strong>), dichas disposiciones
              constituyen el marco jurídico vigente que regula el tratamiento de
              datos personales en México. En caso de reformas o cambios
              normativos, SolCont MX actualizará este documento para reflejar
              las nuevas obligaciones legales.
            </p>
          </section>

          {/* 6. Transferencias */}
          <section className="mb-6" id="transferencias">
            <h2 className="text-lg font-semibold text-slate-900 mb-2">
              6. Transferencia de datos personales
            </h2>
            <p className="text-sm text-slate-700 mb-2">
              SolCont MX no vende ni comercializa sus datos personales. Solo
              podrá compartirlos con terceros en los siguientes casos:
            </p>
            <ul className="list-disc list-inside text-sm text-slate-700 space-y-1 mb-2">
              <li>
                Con autoridades fiscales, administrativas o judiciales, cuando
                exista un requerimiento formal o una obligación legal.
              </li>
              <li>
                Con auxiliares o colaboradores contables para la correcta
                prestación del servicio, bajo estrictos acuerdos de
                confidencialidad.
              </li>
              <li>
                Con instituciones financieras, exclusivamente cuando sea
                necesario para aclaraciones relativas a información contable o
                fiscal.
              </li>
            </ul>
            <p className="text-xs text-slate-500">
              En todos los casos, la transferencia se realizará conforme a los
              criterios de licitud, consentimiento, información, calidad,
              finalidad, lealtad, proporcionalidad y responsabilidad previstos
              por la LFPDPPP.
            </p>
          </section>

          {/* 7. Derechos ARCO */}
          <section className="mb-6" id="arco">
            <h2 className="text-lg font-semibold text-slate-900 mb-2">
              7. Derechos ARCO (Acceso, Rectificación, Cancelación y Oposición)
            </h2>
            <p className="text-sm text-slate-700 mb-2">
              Usted tiene derecho a conocer qué datos personales tenemos de
              usted, para qué los utilizamos y las condiciones del uso que les
              damos (Acceso). Asimismo, tiene derecho a solicitar la
              corrección de su información personal (Rectificación), que la
              eliminemos de nuestros registros o bases de datos cuando considere
              que no está siendo utilizada conforme a los principios, deberes y
              obligaciones legales (Cancelación), así como a oponerse al uso de
              sus datos para fines específicos (Oposición).
            </p>

            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 flex gap-3 mt-2">
              <UserCheck className="w-6 h-6 text-sky-600 mt-1" />
              <div>
                <p className="text-sm font-semibold text-slate-900">
                  ¿Cómo ejercer sus derechos ARCO?
                </p>
                <p className="text-xs text-slate-700 mt-1">
                  Puede ejercer sus derechos ARCO enviando una solicitud al
                  correo:
                </p>
                <p className="text-xs text-sky-600 font-semibold mt-1">
                  isaias.lezama@solcont.com.mx
                </p>
                <p className="text-xs text-slate-600 mt-1">
                  La solicitud deberá incluir: nombre completo, medio para
                  recibir notificaciones, descripción clara del derecho que desea
                  ejercer, y copia de una identificación oficial vigente. El
                  plazo de respuesta será conforme a los términos establecidos
                  por la LFPDPPP y su Reglamento.
                </p>
              </div>
            </div>
          </section>

          {/* 8. Medidas de seguridad */}
          <section className="mb-6" id="seguridad">
            <h2 className="text-lg font-semibold text-slate-900 mb-2">
              8. Medidas de seguridad
            </h2>
            <p className="text-sm text-slate-700 mb-2">
              SolCont MX implementa medidas de seguridad administrativas,
              técnicas y físicas para proteger sus datos personales contra daño,
              pérdida, alteración, destrucción o uso, acceso o tratamiento no
              autorizado.
            </p>

            <div className="grid gap-4 md:grid-cols-3">
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-xs text-slate-700">
                <Lock className="w-5 h-5 text-sky-600 mb-2" />
                <p className="font-semibold">Controles de acceso</p>
                <p className="mt-1">
                  Acceso restringido a la información solo al personal que
                  requiere conocerla.
                </p>
              </div>
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-xs text-slate-700">
                <FileText className="w-5 h-5 text-sky-600 mb-2" />
                <p className="font-semibold">Gestión de documentos</p>
                <p className="mt-1">
                  Resguardo y, en su momento, destrucción segura de papeles de
                  trabajo y archivos físicos.
                </p>
              </div>
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-xs text-slate-700">
                <Shield className="w-5 h-5 text-sky-600 mb-2" />
                <p className="font-semibold">Seguridad digital</p>
                <p className="mt-1">
                  Uso de contraseñas, filtros y buenas prácticas en medios
                  electrónicos para reducir riesgos de accesos no autorizados.
                </p>
              </div>
            </div>
          </section>

          {/* 9. Conservación de datos */}
          <section className="mb-6" id="conservacion">
            <h2 className="text-lg font-semibold text-slate-900 mb-2">
              9. Conservación y eliminación de datos
            </h2>
            <p className="text-sm text-slate-700 mb-2">
              Sus datos personales serán conservados únicamente durante el
              tiempo necesario para:
            </p>
            <ul className="list-disc list-inside text-sm text-slate-700 space-y-1">
              <li>Cumplir las finalidades descritas en este Aviso.</li>
              <li>Cumplir obligaciones legales y fiscales.</li>
              <li>Atender posibles aclaraciones o requerimientos.</li>
            </ul>
            <p className="text-sm text-slate-700 mt-2">
              Una vez concluido el plazo de conservación, los datos serán
              eliminados o anonimizados mediante mecanismos seguros, conforme a
              las disposiciones aplicables.
            </p>
          </section>

          {/* 10. Medios electrónicos */}
          <section className="mb-6" id="medios-electronicos">
            <h2 className="text-lg font-semibold text-slate-900 mb-2">
              10. Uso de medios electrónicos y mensajería
            </h2>
            <p className="text-sm text-slate-700 mb-2">
              La comunicación con SolCont MX puede incluir medios electrónicos
              como correo, mensajería instantánea y otras plataformas. Si bien
              implementamos buenas prácticas para resguardar la información, el
              Cliente reconoce que ningún medio digital es completamente libre
              de riesgo.
            </p>
            <p className="text-sm text-slate-700">
              Al utilizar estos medios, usted acepta el uso de dichos canales
              para el intercambio de información relacionada con los servicios
              contratados.
            </p>
          </section>

          {/* 11. Modificaciones */}
          <section className="mb-6" id="modificaciones">
            <h2 className="text-lg font-semibold text-slate-900 mb-2">
              11. Modificaciones a este Aviso de Privacidad
            </h2>
            <p className="text-sm text-slate-700">
              SolCont MX podrá modificar o actualizar este Aviso de Privacidad
              en cualquier momento, para atender cambios legales, internos o
              nuevos requerimientos para la prestación de servicios.
            </p>
          </section>
          {/* 12. Uso indebido de grabaciones, comunicaciones e información confidencial */}
          <section className="mb-6" id="grabaciones">
            <h2 className="text-lg font-semibold text-slate-900 mb-2">
              12. Uso indebido de grabaciones, comunicaciones e información confidencial
            </h2>

            <p className="text-sm text-slate-700 mb-2">
              El Cliente reconoce que toda comunicación, información técnica, fiscal,
              administrativa, operativa o documental proporcionada por SolCont MX es de
              carácter confidencial y forma parte del trabajo profesional protegido por
              las leyes aplicables en México.
            </p>

            <p className="text-sm text-slate-700 mb-2">
              Queda estrictamente prohibido grabar, almacenar, duplicar, transmitir,
              distribuir, publicar o difundir por cualquier medio (incluyendo pero no
              limitado a audio, video, capturas de pantalla, mensajes, documentos,
              conversaciones de WhatsApp, correos electrónicos o cualquier material
              derivado del servicio) sin la previa y expresa autorización por escrito de
              SolCont MX.
            </p>

            <p className="text-sm text-slate-700 mb-2">
              El uso no autorizado de la voz, imagen, conversaciones, documentos,
              material contable o fiscal del despacho constituye una violación a la
              <strong> Ley Federal de Protección de Datos Personales en Posesión de los
                Particulares (LFPDPPP)</strong>, al <strong> Código Penal Federal </strong>
              en sus disposiciones relativas al uso indebido de información y violación
              de la intimidad personal, así como a los derechos de propiedad intelectual
              y de autor correspondientes.
            </p>

            <p className="text-sm text-slate-700">
              La grabación, difusión o utilización de material sin consentimiento podrá
              dar lugar a responsabilidades de carácter civil, administrativo o penal,
              incluyendo la reparación de daños y perjuicios, conforme a la legislación
              vigente. SolCont MX se reserva el derecho de iniciar las acciones legales
              correspondientes en caso de incumplimiento.
            </p>
          </section>
          {/* 13. SolCont MX como nombre comercial y responsables profesionales */}
          <section id="responsables-final" className="mt-6 border-t border-slate-200 pt-4">
            <h2 className="text-lg font-semibold text-slate-900 mb-2">
              13. SolCont MX como nombre comercial y responsables profesionales
            </h2>

            <p className="text-sm text-slate-700 mb-2">
              El Cliente reconoce que <strong>SolCont MX</strong> es únicamente un nombre
              comercial utilizado para ofrecer servicios profesionales de asesoría
              contable, fiscal y administrativa, y que no constituye una persona moral ni
              una entidad jurídica independiente.
            </p>

            <p className="text-sm text-slate-700 mb-2">
              Los responsables directos de la prestación de servicios, del tratamiento y
              resguardo de los datos personales, y de todas las obligaciones derivadas de
              este documento, son las siguientes personas físicas:
            </p>

            <ul className="list-disc list-inside text-sm text-slate-700 space-y-1">
              <li>
                <strong>Lic. Elizabeth Zavaleta Castillo</strong>, responsable de procesos
                administrativos y fiscales.
              </li>
              <li>
                <strong>Ing. Isaias Lezama Aponte</strong>, responsable de consultoría,
                análisis, comunicación operativa y soluciones digitales.
              </li>
            </ul>

            <p className="text-sm text-slate-700 mt-2">
              Ambos profesionistas actúan de forma conjunta bajo el nombre comercial
              SolCont MX y asumen plenamente la responsabilidad profesional conforme a la
              <strong> Ley Federal de Protección de Datos Personales en Posesión de los
                Particulares (LFPDPPP)</strong>, su Reglamento, el Código Civil Federal, el
              Código de Comercio y demás normatividad aplicable vigente a la fecha.
            </p>

            <p className="text-sm text-slate-700 mt-2">
              El uso del nombre “SolCont MX” en este documento se entiende únicamente como
              referencia operativa y comercial, sin que implique la existencia de una
              persona moral distinta a los responsables antes mencionados.
            </p>
          </section>
          {/* 14. Aceptación */}
          <section id="aceptacion" className="mt-6 border-t border-slate-200 pt-4">
            <p className="text-sm text-slate-700">
              Al proporcionar datos personales a SolCont MX, contratar nuestros
              servicios, compartir documentación fiscal o continuar interactuando
              con cualquiera de nuestros canales, usted reconoce que ha leído,
              comprendido y aceptado el contenido de este Aviso de Privacidad,
              emitido conforme a la legislación aplicable vigente a la fecha.
            </p>
          </section>
        </div>
      </main>

      <footer className="bg-white border-t border-slate-200 py-3">
        <div className="max-w-4xl mx-auto px-4 flex items-center justify-between text-xs text-slate-500">
          <span>© {new Date().getFullYear()} SolCont MX. Todos los derechos reservados.</span>
          <span>Última actualización: 01 de enero de 2025</span>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPage;